import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PROJECT_MANAGEMENT_URL } from '@insite-store360/shared';
import { Observable, catchError } from 'rxjs';
import { ErrorHandlerService } from './error-handler.service';

export interface ProbabilityItem {
  id: number;
  name: string;
  createdDateTime?: Date | string;
  updatedDateTime?: Date | string;
}

@Injectable({
  providedIn: 'root',
})
export class ProbabilityService {
  private readonly URL_PATH = `${PROJECT_MANAGEMENT_URL}/probability`;

  constructor(
    private http: HttpClient,
    private errorHandler: ErrorHandlerService
  ) { }

  /**
   * Get all probabilities for dropdown (with search, no country filter)
   */
  public getAllProbabilitiesForDropdown(searchFilter?: string): Observable<{ result: { id: number; name: string }[] }> {
    let queryParams = new HttpParams();
    if (searchFilter && searchFilter.trim() !== '') {
      queryParams = queryParams.set('searchFilter', searchFilter.trim());
    }
    // Country filtering removed - probabilities are now global
    return this.http
      .get<{ result: { id: number; name: string }[] }>(`${this.URL_PATH}/getProbabilityByCountryId`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get all probabilities with pagination
   */
  public getAll(
    pageIndex?: number,
    pageSize?: number,
    searchValue?: string
  ): Observable<{ data: ProbabilityItem[]; paginatorValue: { totalData: number; totalCount: number } }> {
    let params = new HttpParams();
    if (pageIndex !== undefined && pageIndex !== null) {
      params = params.set('pageIndex', pageIndex.toString());
    }
    if (pageSize !== undefined && pageSize !== null) {
      params = params.set('pageSize', pageSize.toString());
    }
    if (searchValue && searchValue.trim() !== '') {
      params = params.set('searchValue', searchValue.trim());
    }
    return this.http
      .get<{ data: ProbabilityItem[]; paginatorValue: { totalData: number; totalCount: number } }>(`${this.URL_PATH}/getAll`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Create a new probability
   */
  public create(name: string): Observable<{ message: string; result: ProbabilityItem }> {
    return this.http
      .post<{ message: string; result: ProbabilityItem }>(`${this.URL_PATH}/create`, { name })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Update an existing probability
   */
  public update(id: number, name: string): Observable<{ message: string; result: ProbabilityItem }> {
    return this.http
      .put<{ message: string; result: ProbabilityItem }>(`${this.URL_PATH}/update/${id}`, { name })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Delete a probability
   */
  public delete(id: number): Observable<{ message: string }> {
    return this.http
      .delete<{ message: string }>(`${this.URL_PATH}/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }
}

